"use client";

import { createContext, useContext, useEffect, useMemo } from "react";

import { NavPanelShell } from "@/components/nav/NavPanel";
import { AssemblyContextProvider } from "@/context/AssemblyStreaming";
import { MicrophoneContextProvider } from "@/context/MicrophoneAssembly";
import { SABINE_TITLE } from "@/lib/sabine";
import { NAV_COLUMN_WIDTH } from "@/components/nav/nav-styles";
import { cn } from "@/lib/utils";

const CoachPaneContext = createContext({
  open: true,
  onClose: null,
  isFullscreen: false,
  onFullscreenChange: null,
  headerSlot: null,
});

/**
 * What the chat inside the pane needs from it: whether it is showing, the
 * close and fullscreen controls its header offers, and `headerSlot`, the
 * element in the navy top bar its header renders into while the pane sits
 * beside the rail (null when fullscreen or on a phone, where the header
 * stays in the pane). The defaults let the chat render on its own.
 */
export function useCoachPane() {
  return useContext(CoachPaneContext);
}

/**
 * Sabine's column beside the icon rail: the page's registered chat, on the
 * same cream as the menu panel it stands in for, down to the foot of the
 * screen. It is a `NavPanelShell`, so it slides to zero width and goes inert
 * when closed, and the chat inside stays mounted with its thread. Signed in,
 * SideNav's `className` moves it under the navy top bar, where her header
 * sits (`headerSlot`), and makes it a cream column (`.nav-column`), which
 * owns the corner where it meets the navy and the sheet.
 * Fullscreen is the same element covering the viewport, which is also how it
 * opens below `md`, where there is no rail to sit beside. Escape inside it
 * closes it. The voice
 * providers the composer's mic needs live here, once, for every page.
 */
export default function NavCoachPane({
  open,
  fullscreen,
  onClose,
  onFullscreenChange,
  headerSlot = null,
  className,
  children,
}) {
  const value = useMemo(
    () => ({
      open,
      onClose,
      isFullscreen: fullscreen,
      onFullscreenChange,
      headerSlot: fullscreen ? null : headerSlot,
    }),
    [open, onClose, fullscreen, onFullscreenChange, headerSlot],
  );

  // Covering the viewport, it also holds the page still: the frame behind is
  // inert (SideNav), and the body does not scroll under it.
  useEffect(() => {
    if (!fullscreen) return undefined;
    const { style } = document.body;
    const previous = style.overflow;
    style.overflow = "hidden";
    return () => {
      style.overflow = previous;
    };
  }, [fullscreen]);

  const onKeyDown = (event) => {
    // A dialog the chat opened portals out of this element but bubbles
    // through it in React; its Escape is its own.
    if (event.key !== "Escape" || !event.currentTarget.contains(event.target)) {
      return;
    }
    event.stopPropagation();
    onClose?.();
  };

  return (
    <div
      onKeyDown={onKeyDown}
      // Covering the viewport, it is a modal, and a scroll inside never
      // chains to the page.
      role={fullscreen ? "dialog" : undefined}
      aria-modal={fullscreen || undefined}
      aria-label={fullscreen ? SABINE_TITLE : undefined}
      className={cn(
        "bg-surface-chrome print:hidden",
        fullscreen
          ? "fixed inset-0 z-50 flex overscroll-contain"
          : cn(
              "hidden md:sticky md:top-0 md:flex md:h-dvh md:shrink-0 md:self-start",
              className,
            ),
      )}
    >
      <NavPanelShell
        open={open}
        width={fullscreen ? "w-full" : NAV_COLUMN_WIDTH}
        label={SABINE_TITLE}
      >
        <CoachPaneContext.Provider value={value}>
          <MicrophoneContextProvider>
            <AssemblyContextProvider>{children}</AssemblyContextProvider>
          </MicrophoneContextProvider>
        </CoachPaneContext.Provider>
      </NavPanelShell>
    </div>
  );
}
